import React, { createContext, useContext, useEffect, useState } from 'react';
import api from './api';

const AuthCtx = createContext(null);

// Feb 2026 — the AuthProvider now understands two identity flavours:
//   • Seller (owner) — token from /api/auth/login   → GET /api/auth/me
//   • Team member    — token from /api/team/login  → GET /api/team/me
// The `fab_is_team_member` localStorage flag decides which /me to hit.
// `seller` in the context is always the parent-seller doc (so gates keyed
// on seller.plan_tier, is_admin, etc. keep working). `teamMember` carries
// the assigned departments so the router + sidebar can gate the UI.
export function AuthProvider({ children }) {
  const [seller, setSeller] = useState(null);
  const [teamMember, setTeamMember] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    const token = localStorage.getItem('fab_token');
    if (!token) { setSeller(null); setTeamMember(null); setLoading(false); return; }
    const isTeamMember = localStorage.getItem('fab_is_team_member') === '1';
    try {
      if (isTeamMember) {
        const { data } = await api.get('/team/me');
        // Present the parent seller so downstream gates read plan_tier etc.
        setSeller({ ...(data.seller || {}), _is_team_member: true });
        setTeamMember({
          id: data.team_member?.id,
          name: data.team_member?.name,
          departments: data.departments || [],
        });
      } else {
        const { data } = await api.get('/auth/me');
        setSeller(data);
        setTeamMember(null);
      }
    } catch {
      // If the /me call fails (invalid/revoked token), clear the session.
      localStorage.removeItem('fab_token');
      localStorage.removeItem('fab_is_team_member');
      setSeller(null);
      setTeamMember(null);
    }
    setLoading(false);
  };

  useEffect(() => { refresh(); }, []);

  const login = async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    localStorage.setItem('fab_token', data.token);
    localStorage.removeItem('fab_is_team_member');
    await refresh();
  };

  const signup = async (payload) => {
    const { data } = await api.post('/auth/signup', payload);
    localStorage.setItem('fab_token', data.token);
    localStorage.removeItem('fab_is_team_member');
    await refresh();
  };

  const teamLogin = async (phone, password) => {
    const { data } = await api.post('/team/login', { phone, password });
    localStorage.setItem('fab_token', data.token);
    localStorage.setItem('fab_is_team_member', '1');
    await refresh();
    return data;
  };

  const logout = () => {
    const wasTeam = localStorage.getItem('fab_is_team_member') === '1';
    localStorage.removeItem('fab_token');
    localStorage.removeItem('fab_is_team_member');
    setSeller(null);
    setTeamMember(null);
    // Team members land back on the Team Login page; sellers on the marketing site.
    window.location.href = wasTeam ? '/team-login' : '/';
  };

  return (
    <AuthCtx.Provider value={{ seller, teamMember, loading, login, signup, teamLogin, logout, refresh }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
